import React, { useState, useEffect } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import moment from "moment";
import { Loader } from "components/Loader";
import sessionHandling from "services/utils/notificationUtils";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import {
  themes,
  tableContainerSx,
  tableCellFontSize,
  optionalAvathar,
} from "services/constants";
import { RemoveRedEye } from "@mui/icons-material";
import { InfoMessage } from "components/CustomComponents/CustomButton";
import TripDetailScreen from "./TripDetails";
import BSDateRangePicker from "pages/MyZone/Status/DateRangePicker";
import { tooltipStyleProps } from "services/constants/Styles";
import { FilterProvider } from "pages/FilterContext";

const TripHistory = () => {
  const {
    domain,
    token,
    userDetails,
    globalSearchValue,
    topbarCompanyId,
    userScreenAccess,
  } = useSelector((state) => state.tokenReducer);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showMap, setShowMap] = useState(false);
  const [overallTripDetails, setOverallTripDetails] = useState([]);
  const [masterDataForSearch, setMasterDataForSearch] = useState([]);
  const [currentTrip, setCurrentTrip] = useState("");
  const [startDate, setStartDate] = useState(moment().format("DD/MM/YYYY"));
  const [endDate, setEndDate] = useState(moment().format("DD/MM/YYYY"));
  const [selUser, setSeluser] = useState("All Users");
  const [selUserId, setSelUserId] = useState("99999");
  const [allUsers, setAllUsers] = useState([]);
  const [showAutoCompleteAvathar, setShowAutoCompleteAvathar] = useState(false);

  const shiftModule = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Track",
  );

  const subModuleId = shiftModule?.submodules?.find(
    (sub) => sub.sub_module_name === "Track",
  )?.sub_module_id;

  useEffect(() => {
    const fetchTrips = async () => {
      try {
        setIsLoading(true);

        const sDate = startDate.split("/").reverse().join("-");
        const eDate = endDate.split("/").reverse().join("-");

        const tripAPI =
          selUserId !== "99999"
            ? `${domain}tracking/trip-details/?start_date=${sDate}&end_date=${eDate}&selected_user_id=${selUserId}&company_id=${topbarCompanyId}`
            : `${domain}tracking/trip-details/?start_date=${sDate}&end_date=${eDate}&company_id=${topbarCompanyId}`;

        const response = await fetch(tripAPI, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
        });

        const res = await response.json();

        if (response.ok) {
          setOverallTripDetails(res || []);
          setMasterDataForSearch(res || []);
        } else if (response.status === 409) {
          setOverallTripDetails([]);
          setMasterDataForSearch([]);
          sessionHandling();
        } else {
          setOverallTripDetails([]);
          setMasterDataForSearch([]);
        }
      } catch (error) {
        setOverallTripDetails([]);
        setMasterDataForSearch([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTrips();
  }, [
    domain,
    token,
    startDate,
    endDate,
    topbarCompanyId,
    userDetails,
    selUserId,
  ]);

  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const response = await fetch(`${domain}user_with_avatar/ `, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
        });
        const res = await response.json();
        if (response.ok) {
          const AllUsersData = [
            {
              id: "99999",
              username: "All Users",
              label: "All Users",
            },
            ...res.map((obj) => ({
              ...obj,
              label: obj.username,
            })),
          ];
          setAllUsers(AllUsersData);
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error(res.error);
        }
      } catch (error) {
        showToast("error", error.message);
      }
    };
    fetchAllUsers();
  }, [domain, token]);

  useEffect(() => {
    const filteredResults = masterDataForSearch?.filter((item) =>
      ["user_name", "date", "formatted_start_time", "formatted_end_time"].some(
        (key) =>
          item[key]
            ?.toString()
            .toLowerCase()
            .includes(globalSearchValue.toString().toLowerCase()),
      ),
    );
    setOverallTripDetails(filteredResults);
    setPage(0);
  }, [globalSearchValue, masterDataForSearch]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const updateUserName = (event, data) => {
    if (data) {
      setSelUserId(data?.id);
      setSeluser(data);
      setShowAutoCompleteAvathar(false);
    } else {
      setShowAutoCompleteAvathar(true);
    }
  };

  return (
    <React.Fragment>
      {!showMap ? (
        <>
          <Mui.Grid container spacing={1} sx={{ mt: 3, mb: 2 }}>
            <Mui.Grid item xs={12} sm={6} md={4} lg={2.5}>
              <Mui.FormControl fullWidth>
                <BSDateRangePicker
                  fromDate={startDate}
                  toDate={endDate}
                  handleStartDate={handleDateChange}
                  showToday
                  includeCurrentDate
                />
              </Mui.FormControl>
            </Mui.Grid>
            <Mui.Grid item xs={12} sm={6} md={4} lg={2.5}>
              <CustomAutoCompleteWithLogo
                dataList={allUsers?.map((obj) => ({
                  ...obj,
                  label: obj?.username,
                  image: obj?.image_url,
                }))}
                selectedValue={selUser}
                updateDataFunction={updateUserName}
                placeHolder="Select user name"
                label="Users"
                showAvathar={showAutoCompleteAvathar}
                setShowAutoCompleteAvathar={setShowAutoCompleteAvathar}
              />
            </Mui.Grid>
          </Mui.Grid>

          {isLoading ? (
            <Loader info="Loading..." />
          ) : (
            <>
              <Mui.Grid container sx={{ overflow: "hidden", mt: 2 }}>
                <Mui.TableContainer component={Mui.Paper} sx={tableContainerSx}>
                  <Mui.Table>
                    <Mui.TableHead
                      sx={{
                        backgroundColor: themes.primary,
                        position: "sticky",
                        top: -1,
                        zIndex: 10,
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      <Mui.TableRow
                        maxWidth="xl"
                        align="left"
                        sx={{
                          "& .MuiTableCell-root": {
                            height: "15px",
                            padding: "3px 15px",
                          },
                        }}
                      >
                        <Mui.TableCell
                          sx={{
                            color: themes.blackColor,
                            fontWeight: "bold",
                            fontSize: tableCellFontSize,
                          }}
                        >
                          Date
                        </Mui.TableCell>
                        <Mui.TableCell
                          sx={{
                            color: themes.blackColor,
                            fontWeight: "bold",
                            fontSize: tableCellFontSize,
                          }}
                        >
                          Trip Name
                        </Mui.TableCell>
                        <Mui.TableCell
                          sx={{
                            color: themes.blackColor,
                            fontWeight: "bold",
                            fontSize: tableCellFontSize,
                          }}
                        >
                          Employee Name
                        </Mui.TableCell>
                        <Mui.TableCell
                          sx={{
                            color: themes.blackColor,
                            fontWeight: "bold",
                            fontSize: tableCellFontSize,
                          }}
                        >
                          Start Time
                        </Mui.TableCell>
                        <Mui.TableCell
                          sx={{
                            color: themes.blackColor,
                            fontWeight: "bold",
                            fontSize: tableCellFontSize,
                          }}
                        >
                          End Time
                        </Mui.TableCell>
                        <Mui.TableCell
                          sx={{
                            color: themes.blackColor,
                            fontWeight: "bold",
                            fontSize: tableCellFontSize,
                          }}
                        >
                          Distance Cover
                        </Mui.TableCell>
                        <Mui.TableCell
                          sx={{
                            color: themes.blackColor,
                            fontWeight: "bold",
                            fontSize: tableCellFontSize,
                          }}
                        >
                          View Trip
                        </Mui.TableCell>
                      </Mui.TableRow>
                    </Mui.TableHead>
                    <Mui.TableBody>
                      {overallTripDetails?.length === 0 ? (
                        <InfoMessage dataLength={masterDataForSearch} />
                      ) : (
                        overallTripDetails
                          ?.slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                          )
                          ?.map((item, index) => (
                            <Mui.TableRow
                              key={item?.id}
                              sx={{
                                "& .MuiTableCell-root": {
                                  height: "10px",
                                  padding: "3px 15px",
                                },
                              }}
                            >
                              <Mui.TableCell
                                sx={{
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  maxWidth: 100,
                                }}
                              >
                                <Mui.Tooltip
                                  arrow
                                  slotProps={tooltipStyleProps}
                                  title={item?.date}
                                >
                                  <Mui.Typography
                                    sx={{
                                      overflow: "hidden",
                                      whiteSpace: "nowrap",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {item?.date}
                                  </Mui.Typography>
                                </Mui.Tooltip>
                              </Mui.TableCell>
                              <Mui.TableCell
                                sx={{
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  maxWidth: 100,
                                }}
                              >
                                <Mui.Tooltip
                                  arrow
                                  slotProps={tooltipStyleProps}
                                  title={item?.raw_trip_id}
                                >
                                  <Mui.Typography
                                    sx={{
                                      overflow: "hidden",
                                      whiteSpace: "nowrap",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {item?.raw_trip_id}
                                  </Mui.Typography>
                                </Mui.Tooltip>
                              </Mui.TableCell>
                              <Mui.TableCell
                                sx={{
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  maxWidth: 100,
                                }}
                              >
                                <Mui.Tooltip
                                  arrow
                                  slotProps={tooltipStyleProps}
                                  title={item?.user_name}
                                  disableHoverListener={!item?.user_name}
                                >
                                  <Mui.Stack direction="Row">
                                    <Mui.Avatar
                                      sx={{ width: 23, height: 23 }}
                                      alt={item?.user_name
                                        ?.charAt(0)
                                        .toUpperCase()}
                                      src={item?.image_url ?? optionalAvathar}
                                    />
                                    <Mui.Typography
                                      sx={{
                                        overflow: "hidden",
                                        whiteSpace: "nowrap",
                                        textOverflow: "ellipsis",
                                        paddingLeft: 1,
                                      }}
                                    >
                                      {item?.user_name}
                                    </Mui.Typography>
                                  </Mui.Stack>
                                </Mui.Tooltip>
                              </Mui.TableCell>
                              <Mui.TableCell
                                sx={{
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  maxWidth: 100,
                                }}
                              >
                                <Mui.Typography
                                  sx={{
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {item?.formatted_start_time
                                    ? item?.formatted_start_time
                                    : "N/A"}
                                </Mui.Typography>
                              </Mui.TableCell>
                              <Mui.TableCell
                                sx={{
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  maxWidth: 100,
                                }}
                              >
                                <Mui.Typography
                                  sx={{
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {item?.formatted_end_time
                                    ? item?.formatted_end_time
                                    : "N/A"}
                                </Mui.Typography>
                              </Mui.TableCell>
                              <Mui.TableCell
                                sx={{
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  maxWidth: 100,
                                }}
                              >
                                <Mui.Typography
                                  sx={{
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {item?.distance_cover
                                    ? `${item?.distance_cover} km`
                                    : `0.00 km`}
                                </Mui.Typography>
                              </Mui.TableCell>
                              <Mui.TableCell
                                sx={{
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  maxWidth: 100,
                                }}
                              >
                                <Mui.IconButton
                                  onClick={() => {
                                    setCurrentTrip(item);
                                    setShowMap(true);
                                  }}
                                >
                                  <RemoveRedEye />
                                </Mui.IconButton>
                              </Mui.TableCell>
                            </Mui.TableRow>
                          ))
                      )}
                    </Mui.TableBody>
                  </Mui.Table>
                </Mui.TableContainer>
              </Mui.Grid>

              {overallTripDetails?.length > 10 && (
                <Mui.TablePagination
                  className="custom-pagination"
                  component="div"
                  rowsPerPageOptions={[25, 50, 75, 100]}
                  count={overallTripDetails?.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              )}
            </>
          )}
        </>
      ) : (
        <FilterProvider
          domain={domain}
          userDetails={userDetails}
          subModuleId={subModuleId}
        >
          <TripDetailScreen setShowMap={setShowMap} currentTrip={currentTrip} />
        </FilterProvider>
      )}
    </React.Fragment>
  );
};

export default TripHistory;
